import type { NoteSource } from '@crm/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Loader2, Plus } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api } from '../../lib/client.ts';
import { Button } from '../ui/button.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';
import { isBlankHtml } from '../ui/rich-text.tsx';
import { NoteEditor } from './note-editor.tsx';
import { NOTE_SOURCE_KIND } from './note-origin-filter.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover.tsx';
import { NoteTargetTree } from './note-target-tree.tsx';

/**
 * "Add note" on the project's Overview, a menu like the stepper's Won/Lost buttons: it opens a
 * tree of where the note can go — the project and its company as rows, then Contacts, Deals,
 * Jobs and Candidates folding their records (as the Stage filter's tree) — and the pick opens
 * the dialog that writes the note there. Only the sources the viewer may write to are offered;
 * with none, there is no button.
 */
export function NoteCreateDialog({ sources }: { sources: readonly NoteSource[] }) {
  const qc = useQueryClient();
  const [menu, setMenu] = useState(false);
  const [target, setTarget] = useState<NoteSource | null>(null);
  const [body, setBody] = useState('');
  const [pinned, setPinned] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = () => {
    setTarget(null);
    setBody('');
    setPinned(false);
    setError(null);
  };
  const create = useMutation({
    mutationFn: (t: NoteSource) =>
      api.notes.create({
        entityType: t.entityType,
        entityId: t.entityId,
        body,
        bodyFormat: 'html',
        pinned,
      }),
    onSuccess: async () => {
      close();
      await qc.invalidateQueries({ queryKey: ['notes'] });
    },
    onError: (err) => setError(describeError(err)),
  });
  const writable = sources.filter((src) => src.canWrite);
  if (writable.length === 0) return null;
  const pick = (t: NoteSource) => {
    setMenu(false);
    setTarget(t);
  };

  return (
    <>
      <Popover open={menu} onOpenChange={setMenu}>
        <PopoverTrigger asChild>
          <Button size="sm" className="h-7 px-2 text-xs" data-testid="add-note">
            <Plus /> Add note <ChevronDown className="opacity-70" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-1.5" data-testid="note-target-menu">
          <NoteTargetTree sources={writable} onPick={pick} label="Add note to" />
        </PopoverContent>
      </Popover>
      <Dialog open={target !== null} onOpenChange={(open) => !open && close()}>
        <DialogContent className="max-w-3xl" data-testid="note-create-dialog">
          <DialogHeader>
            <DialogTitle>New note</DialogTitle>
            <DialogDescription>
              On the {target && NOTE_SOURCE_KIND[target.entityType].toLowerCase()}
              {target?.entityType !== 'projects' && ` ${target?.label ?? ''}`}
            </DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (target && !isBlankHtml(body)) create.mutate(target);
            }}
          >
            {target && (
              <NoteEditor
                autoFocus
                target={target}
                label="New note"
                value=""
                onChange={setBody}
                onTemplatePinned={setPinned}
                className="max-h-[60vh] min-h-72"
              />
            )}
            <label className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={pinned}
                onChange={(e) => setPinned(e.target.checked)}
                aria-label="Pin this note"
              />
              Pin to top
            </label>
            {error && (
              <p className="text-danger text-sm" role="alert">
                {error}
              </p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isBlankHtml(body) || create.isPending}
                data-testid="save-note"
              >
                {create.isPending && <Loader2 className="animate-spin" />} Save note
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
